import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle2, CircleEllipsis, ConciergeBell, Luggage, Soup, Sparkles, Wrench,
} from "lucide-react";
import { BrandIcon, BrandLogo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { API_BASE } from "@/lib/api";
import { cn } from "@/lib/utils";
import { COPY } from "./ApplyCopy";

const ICONS = { sparkles: Sparkles, soup: Soup, "concierge-bell": ConciergeBell, wrench: Wrench, luggage: Luggage, "circle-ellipsis": CircleEllipsis };
const EMPTY = { name: "", phone: "", role: "", experience: "", pincode: "", city: "", branch: "ANY", languages: [], salary: "", consent: false, company_website: "" };

const Label = ({ children, required }) => (
  <p className="mb-2 text-[15px] font-semibold">
    {children}{required && <span className="ml-0.5 text-destructive">*</span>}
  </p>
);

/** A big tap target. Everything on this form is sized for a thumb on a cheap phone. */
const Choice = ({ active, onClick, children, testId, icon: Icon }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={cn("flex min-h-[64px] flex-1 flex-col items-center justify-center gap-1.5 rounded-2xl border-2 px-3 py-3 text-center text-[13px] font-medium leading-tight transition-all",
      active ? "border-brand-pink bg-brand-pink/10 text-brand-pink-ink shadow-sm"
             : "border-border bg-card hover:border-brand-pink/40")}>
    {Icon && <Icon className={cn("h-5 w-5", active ? "text-brand-pink" : "text-muted-foreground")} />}
    {children}
  </button>
);

const Pill = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={cn("rounded-full border-2 px-4 py-2 text-[14px] font-medium transition-all",
      active ? "border-brand-cyan bg-brand-cyan/15 text-brand-cyan-ink" : "border-border bg-card")}>
    {children}
  </button>
);

export default function Apply() {
  const [sp] = useSearchParams();
  const [lang, setLang] = useState("hi");
  const [cfg, setCfg] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [noPin, setNoPin] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const t = COPY[lang];
  const campaign = sp.get("c") || "";

  useEffect(() => {
    axios.get(`${API_BASE}/public/apply/config`, { params: { b: sp.get("b"), r: sp.get("r"), c: campaign } })
      .then(({ data }) => {
        setCfg(data);
        setF((p) => ({ ...p, branch: data.preselect.branch || "ANY", role: data.preselect.role || "" }));
      })
      .catch(() => setCfg({ live: false, branches: [], roles: [], experience: [], languages: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const branchLabel = useMemo(() => {
    const hit = (cfg?.branches || []).find((b) => b.code === f.branch);
    return hit ? hit.label : null;
  }, [cfg, f.branch]);

  const submit = async (e) => {
    e.preventDefault();
    if (f.name.trim().length < 2) return setError(t.nameBad);
    if (!/^[0-9]{10}$/.test(f.phone.replace(/\D/g, "").slice(-10))) return setError(t.phoneBad);
    if (!f.role) return setError(t.roleBad);
    if (!f.consent) return setError(t.consentMissing);
    setBusy(true);
    setError(null);
    try {
      await axios.post(`${API_BASE}/public/apply`, { ...f, lang, campaign_id: campaign });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || t.failed);
    } finally {
      setBusy(false);
    }
  };

  const Header = (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2.5">
        <BrandIcon />
        <BrandLogo className="h-5" />
      </div>
      <button type="button" onClick={() => setLang(lang === "hi" ? "en" : "hi")}
        data-testid="apply-lang-toggle"
        className="rounded-full border-2 px-3.5 py-1.5 text-[13px] font-semibold hover:bg-accent">
        {t.switchTo}
      </button>
    </div>
  );

  if (cfg && !cfg.live) {
    return (
      <Shell header={Header}>
        <div className="mt-10 rounded-2xl border bg-card p-6 text-center" data-testid="apply-closed">
          <h1 className="font-display text-2xl font-bold">{t.closedTitle}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{t.closedBody}</p>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell header={Header}>
        <div className="mt-8 rounded-2xl border-2 border-brand-teal/40 bg-brand-teal/5 p-6 text-center" data-testid="apply-thanks">
          <CheckCircle2 className="mx-auto h-14 w-14 text-brand-teal" />
          <h1 className="mt-3 font-display text-3xl font-bold">{t.thanksTitle}</h1>
          <p className="mt-2 text-[17px] font-medium">{t.thanksBody}</p>
          <p className="mt-2 text-sm text-muted-foreground">{t.thanksNote}</p>
          <Button variant="outline" className="mt-6 h-12 w-full text-[15px]" data-testid="apply-another"
            onClick={() => { setDone(false); setF({ ...EMPTY, branch: cfg?.preselect?.branch || "ANY" }); }}>
            {t.another}
          </Button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell header={Header}>
      <div className="mt-5">
        <h1 className="font-display text-3xl font-bold leading-tight" data-testid="apply-title">{t.title}</h1>
        <p className="mt-1.5 text-[15px] text-muted-foreground">{t.subtitle}</p>
        {branchLabel && (
          <p className="mt-3 inline-flex rounded-full bg-brand-yellow/30 px-3 py-1 text-[13px] font-semibold text-brand-yellow-ink" data-testid="apply-branch-chip">
            {branchLabel}
          </p>
        )}
      </div>

      <form onSubmit={submit} className="mt-6 space-y-6" data-testid="apply-form">
        <div>
          <Label required>{t.name}</Label>
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
            placeholder={t.namePlaceholder} className="h-14 text-[16px]" data-testid="apply-name" />
        </div>
        <div>
          <Label required>{t.phone}</Label>
          <Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })}
            inputMode="numeric" placeholder={t.phonePlaceholder} className="h-14 text-[16px]"
            data-testid="apply-phone" />
        </div>
        <div>
          <Label required>{t.role}</Label>
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
            {(cfg?.roles || []).map((r) => (
              <Choice key={r.key} active={f.role === r.key} icon={ICONS[r.icon]}
                onClick={() => setF({ ...f, role: r.key })} testId={`apply-role-${r.key}`}>
                {r[lang]}
              </Choice>
            ))}
          </div>
        </div>
        <div>
          <Label>{t.experience}</Label>
          <div className="flex flex-wrap gap-2">
            {(cfg?.experience || []).map((x) => (
              <Pill key={x.key} active={f.experience === x.key}
                onClick={() => setF({ ...f, experience: x.key })} testId={`apply-exp-${x.key}`}>
                {x[lang]}
              </Pill>
            ))}
          </div>
        </div>
        <div>
          <Label>{noPin ? t.city : t.pincode}</Label>
          {noPin ? (
            <Input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })}
              placeholder={t.cityPlaceholder} className="h-14 text-[16px]" data-testid="apply-city" />
          ) : (
            <Input value={f.pincode} onChange={(e) => setF({ ...f, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })}
              inputMode="numeric" placeholder={t.pincodePlaceholder} className="h-14 text-[16px]"
              data-testid="apply-pincode" />
          )}
          <button type="button" onClick={() => { setNoPin((v) => !v); setF({ ...f, pincode: "", city: "" }); }}
            className="mt-2 text-[13px] font-medium text-brand-cyan-ink underline" data-testid="apply-nopin">
            {noPin ? t.pincode : t.dontKnow}
          </button>
        </div>
        <div>
          <Label>{t.branch}</Label>
          <div className="flex flex-wrap gap-2">
            <Pill active={f.branch === "ANY"} onClick={() => setF({ ...f, branch: "ANY" })} testId="apply-branch-ANY">
              {t.anyBranch}
            </Pill>
            {(cfg?.branches || []).map((b) => (
              <Pill key={b.code} active={f.branch === b.code} onClick={() => setF({ ...f, branch: b.code })}
                testId={`apply-branch-${b.code}`}>
                {b.label}{b.opening_soon ? ` · ${t.openingSoon}` : ""}
              </Pill>
            ))}
          </div>
        </div>
        <div>
          <Label>{t.languages}</Label>
          <div className="flex flex-wrap gap-2">
            {(cfg?.languages || []).map((l) => (
              <Pill key={l} active={f.languages.includes(l)} testId={`apply-lang-${l}`}
                onClick={() => setF({ ...f, languages: f.languages.includes(l) ? f.languages.filter((x) => x !== l) : [...f.languages, l] })}>
                {l}
              </Pill>
            ))}
          </div>
        </div>
        <div>
          <Label>{t.salary}</Label>
          <Input value={f.salary} onChange={(e) => setF({ ...f, salary: e.target.value.replace(/\D/g, "").slice(0, 7) })}
            inputMode="numeric" placeholder={t.salaryPlaceholder} className="h-14 text-[16px]"
            data-testid="apply-salary" />
        </div>

        {/* The honeypot. Off-screen, never announced, and a bot that fills it is refused. */}
        <input type="text" name="company_website" tabIndex={-1} autoComplete="off"
          value={f.company_website} onChange={(e) => setF({ ...f, company_website: e.target.value })}
          className="pointer-events-none absolute left-[-9999px] h-0 w-0 opacity-0" aria-hidden="true"
          data-testid="apply-honeypot" />

        <label className="flex cursor-pointer items-start gap-3 rounded-2xl border-2 border-brand-yellow/60 bg-brand-yellow/10 p-4">
          <Checkbox checked={f.consent} onCheckedChange={(v) => setF({ ...f, consent: !!v })}
            className="mt-0.5 h-6 w-6" data-testid="apply-consent" />
          <span className="text-[14px] leading-snug">
            {t.consent}
            <Link to="/apply/privacy" target="_blank" className="ml-1 font-semibold underline" data-testid="apply-privacy-link">
              {t.privacy}
            </Link>
          </span>
        </label>

        {error && (
          <p className="rounded-xl border-2 border-destructive/40 bg-destructive/10 px-4 py-3 text-[14px] font-medium text-destructive" data-testid="apply-error">
            {error}
          </p>
        )}
        <Button type="submit" disabled={busy} className="h-16 w-full text-[17px] font-bold" data-testid="apply-submit">
          {busy ? t.sending : t.submit}
        </Button>
      </form>
    </Shell>
  );
}

export const Shell = ({ children, header }) => (
  <div className="min-h-screen bg-background">
    <div className="brand-stripe h-[4px]" />
    <div className="mx-auto max-w-[520px] px-4 pb-16 pt-4">
      {header}
      {children}
    </div>
  </div>
);
